import { Component, EventEmitter, Inject, Injectable, Input, Output } from '@angular/core';

import { DateAdapter } from '@angular/material/core';

import { Observable } from 'rxjs';

import { LocalDate } from '../types/local-date.type';

import { CommandRelay } from '../../commands/command-relay.class';

import {
	MatDatepickerInputEvent,
	MatDateRangeSelectionStrategy,
	DateRange,
	MAT_DATE_RANGE_SELECTION_STRATEGY,
} from '@angular/material/datepicker';

import { Moment } from 'moment';

import { DateService } from '../../date-service/date.service';
import { JSR310ConversionService } from '../services/jsr310-conversion.service';

@Injectable()
export class WeekSelectionStrategy<D> implements MatDateRangeSelectionStrategy<D> {
	constructor(private dateAdapter: DateAdapter<D>) { }

	selectionFinished(date: D | null): DateRange<D> {
		return this.createWeekRange(date);
	}

	createPreview(activeDate: D | null): DateRange<D> {
		return this.createWeekRange(activeDate);
	}

	private createWeekRange(date: D | null): DateRange<D> {
		if (date) {
			let dayOfWeek = this.dateAdapter.getDayOfWeek(date);
			let offset = dayOfWeek ? (dayOfWeek * -1) + 1 : -6;
			let startOfWeek = this.dateAdapter.addCalendarDays(date, offset);
			let endOfWeek = this.dateAdapter.addCalendarDays(date, offset + 6);
			return new DateRange<D>(startOfWeek, endOfWeek);
		}

		return new DateRange<D>(null, null);
	}
}

@Component({
	selector: 'week-input',
	templateUrl: 'week-input.html',
	styleUrls: ['week-input.css'],
	providers: [
		{
			provide: MAT_DATE_RANGE_SELECTION_STRATEGY,
			useClass: WeekSelectionStrategy
		}
	],
})
export class WeekInputComponent {
	_startDate: Date | null = null;
	
	endDate: Date | null = null;

	weekAsString: string = '';

	constructor( //
		@Inject(DateService) private dateService: DateService,
		@Inject(JSR310ConversionService) private conversionService: JSR310ConversionService
	) { }

	@Input() id!: string;

	@Input() disabled: boolean = false;
	
	// Permet d'initialiser le composant a travers un binding si cela est plus pratique
	@Input() set startDate(localDate: LocalDate | null) {
		this.init(localDate);
	}
	
	private initCommandsRelay = new CommandRelay<LocalDate | null>(data => this.init(data));
	@Input() set initCommands(obs: Observable<LocalDate | null> | null | undefined) {
		this.initCommandsRelay.register(obs);
	}

	@Output() localDateChange = new EventEmitter<number[] | null>();
	
	private init(localDate: LocalDate | null) {
		let dateJs = this.conversionService.fromNullableLocalDate(localDate);
		if (dateJs) {
			// Calcul du premier jour de la semaine passee en parametre
			let dayOfWeek = dateJs.getDay();
			let offset = dayOfWeek ? (dayOfWeek * -1) + 1 : -6;
			let dateNum = dateJs.getDate();
			let startOfWeek = new Date(dateJs.setDate(dateNum + offset));
			
			if (!this._startDate || startOfWeek.getDate() != this._startDate.getDate()) {
				this._startDate = startOfWeek;
				this.endDate =  new Date(dateJs.setDate(dateNum + offset + 6));
				this.innerStartDateChanged(startOfWeek);
			}
		} else {
			if (this._startDate) {
				this._startDate = null;
				this.endDate =  null;
				this.innerStartDateChanged(null);
			}
		}
 	}

	onStartDateChange(event: MatDatepickerInputEvent<Moment>) {
		// Extraire la date selectionnee a format JS
		let moment: Moment | null = event.value;
		let date = moment ? moment.toDate() : null;
		
		// Il n'y a pas de two-way binding sur la variable startDate, donc quand
		// la date change, il vaut mieux mettre cette variable a jour programmativement
		this._startDate = date;
		
		this.innerStartDateChanged(date);
	}
	
	private innerStartDateChanged(date: Date| null) {
		// Mettre a jour la representation de la semaine dans l'input
		this.weekAsString = this.dateService.getWeekAsString(date);
		
		// Convertir en LocalDate et emettre l'evenement de changement
		let localDate = this.conversionService.toNullableLocalDate(date);
		this.localDateChange.emit(localDate);
	}
	
	onWeekDeletionRequest() {
		if (this._startDate) {
			this._startDate = null;
			this.endDate =  null;
			this.innerStartDateChanged(null);
		}
	}
}